import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { BLOCKED_STEPS } from "@/components/DesktopNotificationBridge";
import { enableDesktopNotifications, pushSupported } from "@/lib/push";
import { timeAgo } from "@/lib/format";

const FILTERS = ["all", "requests", "attendance", "announcements", "tasks"];
const CATEGORY_LABELS = {
  requests: "Requests & approvals", attendance: "Attendance & breaks", announcements: "Announcements",
  tasks: "Tasks", team: "Shift / team changes", reports: "Weekly reports",
};

export function notificationCategory(n) {
  if (n.category) return n.category;
  const text = `${n.title || ""} ${n.link || ""} ${n.type || ""}`.toLowerCase();
  if (/request|leave|overtime|regulariz/.test(text)) return "requests";
  if (/break|attendance|clock|late/.test(text)) return "attendance";
  if (/announcement|communication/.test(text)) return "announcements";
  if (/task/.test(text)) return "tasks";
  return "team";
}

function Preferences() {
  const [prefs, setPrefs] = useState(null);
  const [permission, setPermission] = useState(pushSupported() ? Notification.permission : "unsupported");
  useEffect(() => {
    api.get("/notify/prefs").then(({ data }) => setPrefs(data)).catch(() => {});
  }, []);
  const toggle = async (cat, channel, value) => {
    const categories = { ...prefs.categories, [cat]: { ...prefs.categories[cat], [channel]: value } };
    setPrefs({ ...prefs, categories });
    try {
      await api.put("/notify/prefs", { categories });
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  if (!prefs) return null;
  return (
    <Card className="p-5" data-testid="notification-preferences">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-lg font-semibold">Delivery preferences</div>
          <div className="text-xs text-muted-foreground">Desktop: {permission} · {prefs.push_devices} device(s) subscribed. If desktop is off, alerts fall back to email.</div>
        </div>
        <div className="flex gap-2">
          {permission !== "granted" && permission !== "unsupported" && (
            <Button size="sm" variant="outline" onClick={async () => setPermission(await enableDesktopNotifications())} data-testid="notification-enable-desktop">Enable desktop</Button>
          )}
          <Button size="sm" className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="notification-send-test"
            onClick={() => api.post("/notify/test").then(() => toast.success("Test notification sent")).catch((e) => toast.error(apiErr(e)))}>
            Send test notification
          </Button>
        </div>
      </div>
      {permission === "denied" && (
        <ul className="mt-3 space-y-1 text-xs text-muted-foreground" data-testid="notification-blocked-steps">
          {BLOCKED_STEPS.map(([b, s]) => <li key={b}><b>{b}:</b> {s}</li>)}
        </ul>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2">Category</th><th>In-app</th><th>Email</th><th>Desktop</th></tr></thead>
          <tbody>
            {Object.entries(prefs.categories).map(([cat, channels]) => (
              <tr key={cat} className="border-t border-border">
                <td className="py-2">{CATEGORY_LABELS[cat] || cat}</td>
                {["in_app", "email", "desktop"].map((channel) => (
                  <td key={channel}><Switch checked={channels[channel]} onCheckedChange={(v) => toggle(cat, channel, v)} data-testid={`pref-${cat}-${channel}`} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => {
    api.get("/notify/items", { params: { category: filter, limit: 100 } }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [filter]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);
  const open = async (n) => {
    await api.post(`/meta/notifications/${n.id}/read`).catch(() => {});
    navigate(n.link || "/");
  };
  return (
    <div className="space-y-5" data-testid="notification-center-page">
      <PageHeader title="Notifications" subtitle={`${data.unread} unread`} />
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((f) => (
              <Button key={f} size="sm" variant={filter === f ? "default" : "ghost"} onClick={() => setFilter(f)}
                className={`capitalize ${filter === f ? "bg-[#255058] hover:bg-[#1c3d43]" : ""}`} data-testid={`notification-page-filter-${f}`}>{f}</Button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => api.post("/notify/items/read-all").then(load)} data-testid="notification-page-mark-all">Mark all read</Button>
        </div>
        {data.items.map((n) => (
          <button key={n.id} type="button" onClick={() => open(n)} data-testid={`notification-page-item-${n.id}`}
            className={`block w-full border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-accent/50 ${n.read ? "" : "bg-[#255058]/5"}`}>
            <div className="flex justify-between gap-3 text-sm font-medium"><span>{n.title}</span><span className="shrink-0 text-xs capitalize text-muted-foreground">{notificationCategory(n)}</span></div>
            {n.body && <div className="mt-0.5 text-xs text-muted-foreground">{n.body}</div>}
            <div className="mt-0.5 text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</div>
          </button>
        ))}
        {!data.items.length && <div className="p-8 text-center text-sm text-muted-foreground">You're all caught up.</div>}
      </Card>
      <Preferences />
    </div>
  );
}
